import { useQueryClient } from '@tanstack/react-query';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { CloseModalButton } from '../../../components/close-modal-button/index';
import { FadeIn } from '../../../components/motion/index';
import { Sheet, SHEET_CLOSE_ANIMATION_MS, SheetContent } from '../../../components/sheet/index';
import { QueryKey } from '../../../constants/index';
import { PendingMutationSettleOutcome } from '../../../hooks/event/pending-events.store';
import { EventHooks, useLocale, useReactForm } from '../../../hooks/index';
import { EditEventModalBodyPlaceholder } from '../components/events-loading-placeholders';
import { formSchema, FormSchema } from '../event-form/event-form.validation';
import { manageReverseVestingStore } from './manage-reverse-vesting.store';
import {
  buildManageReverseVestingUpdateDto,
  isManageReverseVestingEvent,
  manageReverseVestingFormValues,
  manageReverseVestingSubtitle,
} from './manage-reverse-vesting.util';
import { ManageReverseVestingRowsStep } from './manage-reverse-vesting-rows-step';
import { ManageReverseVestingSummaryStep } from './manage-reverse-vesting-summary-step';

export type ManageReverseVestingModalProps = {
  companyId: string;
  invalidateQuery: () => void;
};

/**
 * Manage Reverse Vesting — scoped to the whole event, not the row the "⋮" menu was opened on:
 * every issuance row of the event is listed and saved together.
 *
 * The valuation-period lock is deliberately not a gate here. A locked event opens the same flow
 * with its issuance details frozen; only the repurchase schedules stay editable, and the backend
 * accepts exactly that payload through its restricted-patch path.
 */
export const ManageReverseVestingModal: FC<ManageReverseVestingModalProps> = ({
  companyId,
  invalidateQuery,
}) => {
  const isOpen = manageReverseVestingStore(({ isOpen }) => isOpen);
  const target = manageReverseVestingStore(({ target }) => target);
  const close = manageReverseVestingStore(({ close }) => close);
  const clearTarget = manageReverseVestingStore(({ clearTarget }) => clearTarget);

  const queryClient = useQueryClient();
  const { messagesLocale } = useLocale();
  const { update, isPending } = EventHooks.useUpdate();
  const { event } = EventHooks.useEvent({ companyId, eventId: target?.eventId ?? '' });

  const [step, setStep] = useState<1 | 2>(1);
  const hydratedForEventIdRef = useRef<null | string>(null);

  const { control, reset, setValue, getValues, clearErrors } = useReactForm({
    schema: formSchema,
    mode: 'onChange',
  });

  const manageableEvent = isManageReverseVestingEvent(event) ? event : null;

  useEffect(() => {
    if (!isOpen) {
      hydratedForEventIdRef.current = null;
      setStep(1);
      return;
    }
    if (!manageableEvent || hydratedForEventIdRef.current === target?.eventId) return;

    hydratedForEventIdRef.current = target?.eventId ?? null;
    reset(manageReverseVestingFormValues(manageableEvent));
  }, [isOpen, manageableEvent, reset, target?.eventId]);

  // The sheet stays mounted through its close animation, so the form is cleared afterwards rather
  // than on close — resetting immediately would blank the content mid-slide. The target goes with
  // it, which disables the event query: a closed sheet must not keep refetching an event that a
  // later edit recreated under a new id or a delete removed.
  useEffect(() => {
    if (isOpen) return;

    const timeoutId = setTimeout(() => {
      reset({} as FormSchema);
      clearTarget();
    }, SHEET_CLOSE_ANIMATION_MS);
    return () => clearTimeout(timeoutId);
  }, [clearTarget, isOpen, reset]);

  const handleClose = useCallback(() => {
    close();
  }, [close]);

  const handleSave = useCallback(() => {
    const eventId = target?.eventId;
    if (!manageableEvent || !eventId) return;

    handleClose();

    update(
      {
        companyId,
        eventId,
        data: buildManageReverseVestingUpdateDto(manageableEvent, getValues()),
        onApplySettled: (outcome) => {
          if (outcome === PendingMutationSettleOutcome.APPLIED) {
            toast.success(messagesLocale['REVERSE_VESTING_SUCCESSFULLY_UPDATED']);
          }
          if (outcome === PendingMutationSettleOutcome.FAILED) {
            toast.error(messagesLocale['REVERSE_VESTING_UPDATE_FAILED']);
          }
        },
      },
      {
        onSuccess: () => {
          invalidateQuery();
          queryClient.invalidateQueries({ queryKey: [QueryKey.GET_EVENT_TRANSACTIONS] });
          queryClient.invalidateQueries({ queryKey: [QueryKey.GET_EVENT_TRANSACTIONS_COUNT] });
          queryClient.invalidateQueries({
            queryKey: [QueryKey.GET_STAKEHOLDER_REVERSE_VESTING],
          });
        },
        onError: (error) => {
          toast.error(error.body?.message || messagesLocale['REVERSE_VESTING_UPDATE_FAILED']);
        },
      },
    );
  }, [
    companyId,
    getValues,
    handleClose,
    invalidateQuery,
    manageableEvent,
    messagesLocale,
    queryClient,
    target?.eventId,
    update,
  ]);

  if (!isOpen && !target) return null;

  const isLocked = !(manageableEvent?.canEdit ?? true);
  const isHydrated = !!manageableEvent && hydratedForEventIdRef.current === target?.eventId;

  const subtitle = manageReverseVestingSubtitle(manageableEvent, target?.transactionId);

  return (
    <Sheet onOpenChange={(open) => !open && handleClose()} open={isOpen}>
      <SheetContent
        className="w-full max-w-[500px] border-transparent bg-transparent p-2 shadow-none"
        onInteractOutside={handleClose}
        side="RIGHT"
      >
        <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border-[1px] border-gray-300 bg-gray-100 pb-4">
          <div className="flex h-fit w-full items-center justify-between bg-white px-6 py-3">
            <div className="flex min-w-0 flex-col pr-2">
              <span className="truncate text-xl font-[550] text-gray-700">
                <AppFormattedMessage id="MANAGE_REVERSE_VESTING" />
              </span>
              {subtitle && (
                <span className="block truncate text-xs font-[450] text-gray-500">{subtitle}</span>
              )}
            </div>
            <CloseModalButton onClose={handleClose} />
          </div>
          <div className="flex w-full items-center gap-[5px] bg-white px-6 pb-6">
            {[1, 2].map((barStep) => (
              <div
                className={twMerge(
                  'h-1 w-full rounded-[25px]',
                  barStep <= step ? 'bg-[#12B76A]' : 'bg-gray-100',
                )}
                key={barStep}
              />
            ))}
          </div>

          <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
            {!isHydrated || !manageableEvent ? (
              <EditEventModalBodyPlaceholder />
            ) : (
              <FadeIn className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
                {step === 1 ? (
                  <ManageReverseVestingRowsStep
                    clearErrors={clearErrors}
                    companyId={companyId}
                    control={control}
                    event={manageableEvent}
                    expandedRowId={target?.transactionId}
                    isLocked={isLocked}
                    onCancel={handleClose}
                    onNext={() => setStep(2)}
                    setFormData={setValue}
                  />
                ) : (
                  <ManageReverseVestingSummaryStep
                    data={getValues()}
                    event={manageableEvent}
                    isLoading={isPending}
                    onBack={() => setStep(1)}
                    onCancel={handleClose}
                    onSave={handleSave}
                  />
                )}
              </FadeIn>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
